import {
  PresentationView,
  PresentationConfig,
  PresentationEditData,
} from "@cma/presentation-generation-logic/models";

export class PresentationApiError extends Error {
  public readonly status: number;
  public readonly statusText: string;
  public readonly url: string;
  public readonly method: string;
  public readonly details: any;

  constructor(
    message: string,
    status: number,
    statusText: string,
    url: string,
    method: string,
    details: any,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.method = method;
    this.details = details;
  }
}

export class PresentationApiClient {
  private host: string;
  private defaultHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json;charset=UTF-8",
  };

  constructor() {
    if (!process.env.SIDEKICK_COMPS_API_URL) {
      throw new Error("Variable SIDEKICK_COMPS_API_URL is required");
    }
    this.host = `${process.env.SIDEKICK_COMPS_API_URL}/comps/cma-presentations`;
  }

  private async fetchJSON(
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
    url: string,
    body: any,
    options: {
      emptyResponse?: boolean;
    } = {},
  ) {
    const headers: { [key: string]: string } = { ...this.defaultHeaders };

    const opts = {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : null,
    };

    const emptyResponse = options.emptyResponse === true;

    const response = await fetch(url, { ...opts, credentials: "include" });
    if (response.status >= 200 && response.status < 300) {
      if (!emptyResponse) return response.json();
    } else {
      let errorDetails;
      try {
        errorDetails = await response.json();
      } catch {
        errorDetails = { message: response.statusText };
      }

      const error = new PresentationApiError(
        `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        response.statusText,
        url,
        method,
        errorDetails,
      );

      throw error;
    }
  }

  async getPresentationEdit(id: string): Promise<PresentationEditData> {
    const url = `${this.host}/${encodeURIComponent(id)}/edit`;
    const result = await this.fetchJSON("GET", url, null);
    return result;
  }
  async getPresentationView(id: string): Promise<PresentationView> {
    const url = `${this.host}/${encodeURIComponent(id)}/view`;
    const result = await this.fetchJSON("GET", url, null);
    return result;
  }
  async putPresentationConfig(config: PresentationConfig): Promise<void> {
    const url = `${this.host}/${encodeURIComponent(config.id)}/edit`;
    await this.fetchJSON("PATCH", url, config, { emptyResponse: true });
  }
}
